// Pictures of data displays for the graph skills: a column graph, a line
// graph, a picture graph, a tally, a dot plot, a pie chart and a table.
// Plain React SVG and HTML, like practice-diagram.tsx. None of them prints
// a value on the picture: reading it off the scale or the key is the skill.

import { useRef, type KeyboardEvent } from 'react';
import {
  clipGridLine,
  describeCoordinateGrid,
  type CoordinateGridSpec,
  type GraphDiagramSpec,
} from './practice-graph-data';
import { AnglePictureView } from './practice-angle';
import { AreaModelView } from './practice-area-model';
import { BarModelPictureView } from './practice-bar-model';
import { CirclePartsView } from './practice-circle';
import { ClockPictureView } from './practice-clock';
import { CountPictureView } from './practice-count';
import { CubeStackView } from './practice-cube-stack';
import { CurvedShapeView } from './practice-curved-shape';
import { ShapePairView } from './practice-shape-pair';
import { QuadDiagonalsView } from './practice-quad-diagonals';
import { TreeDiagramView } from './practice-tree-diagram';
import { VennView } from './practice-venn';
import { Axes3dView } from './practice-axes-3d';
import { EverydayObjectView } from './practice-everyday-object';
import { FractionModelView } from './practice-fraction-model';
import { HundredChartView } from './practice-hundred-chart';
import { InformalMeasureView } from './practice-informal-measure';
import { InstrumentPictureView } from './practice-instrument';
import { LineUpView } from './practice-line-up';
import { MeasurePictureView } from './practice-measure';
import { MoneyPictureView } from './practice-money';
import { NumberLineView } from './practice-number-line';
import { PicturePatternView } from './practice-picture-pattern';
import { ShadeGridView } from './practice-shade-grid';
import { ShapePictureView } from './practice-shape';
import { SolidPictureView } from './practice-solid';
import { TenFrameView } from './practice-ten-frame';
import { TurnPictureView } from './practice-turn';

const INK = '#2d4a3e';
const SECOND = '#c98a3c';
const GRID = '#d9ddd5';
const SECTOR_FILLS = ['#2d4a3e', '#c98a3c', '#9fc3b1', '#f0e4c8', '#ffffff'];
// A spinner whose parts are all named after colours is drawn in those
// colours, so a part called Red is red.
const COLOUR_FILLS: Record<string, string> = {
  red: '#d64541',
  blue: '#3a78c9',
  yellow: '#f2cf3c',
  green: '#3f9b4f',
  orange: '#ef8a2c',
  purple: '#8a5bb5',
  pink: '#f19bbd',
  white: '#ffffff',
  black: '#2b2b2b',
  grey: '#9a9a9a',
  gray: '#9a9a9a',
  silver: '#c4c8cc',
  brown: '#8a5a33',
};

function sectorFills(labels: string[]) {
  const named = labels.map((label) => COLOUR_FILLS[label.trim().toLowerCase()]);
  if (named.every(Boolean)) return named as string[];
  return labels.map((_, index) => SECTOR_FILLS[index] ?? '#ffffff');
}

const W = 340;
const LEFT = 48;
const RIGHT = 332;
const BOTTOM = 170;

type ScaledGraph = Extract<
  GraphDiagramSpec,
  { type: 'column_graph' | 'line_chart' }
>;

function Title({ text }: { text: string | null }) {
  if (!text) return null;
  return <p className="mb-1 text-sm font-semibold text-forest-950">{text}</p>;
}

function ScaledGraphView({ graph }: { graph: ScaledGraph }) {
  const top = graph.series ? 30 : 14;
  const height = BOTTOM + 40;
  const marks = Math.round(graph.max / graph.step);
  const yFor = (value: number) => BOTTOM - (value / graph.max) * (BOTTOM - top);
  const band = (RIGHT - LEFT) / graph.columns.length;
  const perColumn = graph.series ? graph.series.length : 1;
  const isColumns = graph.type === 'column_graph';
  const description = `${isColumns ? 'A column graph' : 'A line graph'} of ${graph.yLabel} by ${graph.xLabel}. ${graph.columns
    .map(
      (column) =>
        `${column.label}: ${column.values
          .map((value, index) =>
            graph.series
              ? `${graph.series[index] ?? ''} ${String(value)}`
              : String(value),
          )
          .join(', ')}`,
    )
    .join('; ')}.`;
  const colours = [INK, SECOND];

  return (
    <figure className="m-0">
      <Title text={graph.title} />
      <svg
        role="img"
        aria-label={description}
        viewBox={`0 0 ${String(W)} ${String(height)}`}
        className="h-auto w-full max-w-md"
      >
        {graph.series?.map((name, index) => (
          <g key={name}>
            <rect
              x={LEFT + index * 130}
              y={4}
              width={12}
              height={12}
              fill={colours[index]}
            />
            <text
              x={LEFT + index * 130 + 17}
              y={14}
              className="fill-[#2d4a3e] text-[11px] font-semibold"
            >
              {name}
            </text>
          </g>
        ))}
        {Array.from({ length: marks + 1 }, (_, index) => {
          const value = index * graph.step;
          const y = yFor(value);
          return (
            <g key={value}>
              <line
                x1={LEFT}
                y1={y}
                x2={RIGHT}
                y2={y}
                stroke={index === 0 ? INK : GRID}
                strokeWidth={index === 0 ? 2 : 1}
              />
              <text
                x={LEFT - 6}
                y={y + 4}
                textAnchor="end"
                className="fill-[#2d4a3e] text-[11px] font-semibold"
              >
                {value}
              </text>
            </g>
          );
        })}
        <line
          x1={LEFT}
          y1={top}
          x2={LEFT}
          y2={BOTTOM}
          stroke={INK}
          strokeWidth="2"
        />
        {isColumns &&
          graph.columns.map((column, columnIndex) => {
            const barWidth = (band * 0.7) / perColumn;
            const start = LEFT + columnIndex * band + band * 0.15;
            return column.values.map((value, seriesIndex) => (
              <rect
                key={`${column.label}-${String(seriesIndex)}`}
                x={start + seriesIndex * barWidth}
                y={yFor(value)}
                width={barWidth}
                height={BOTTOM - yFor(value)}
                fill={colours[seriesIndex]}
              />
            ));
          })}
        {!isColumns &&
          Array.from({ length: perColumn }, (_, seriesIndex) => (
            <g key={seriesIndex}>
              <polyline
                fill="none"
                stroke={colours[seriesIndex]}
                strokeWidth="2"
                strokeDasharray={seriesIndex === 1 ? '6 4' : undefined}
                points={graph.columns
                  .map(
                    (column, columnIndex) =>
                      `${String(LEFT + columnIndex * band + band / 2)},${String(yFor(column.values[seriesIndex] ?? 0))}`,
                  )
                  .join(' ')}
              />
              {graph.columns.map((column, columnIndex) => (
                <circle
                  key={column.label}
                  cx={LEFT + columnIndex * band + band / 2}
                  cy={yFor(column.values[seriesIndex] ?? 0)}
                  r="3.5"
                  fill={colours[seriesIndex]}
                />
              ))}
            </g>
          ))}
        {graph.columns.map((column, columnIndex) => (
          <text
            key={column.label}
            x={LEFT + columnIndex * band + band / 2}
            y={BOTTOM + 14}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-[10px] font-semibold"
          >
            {column.label}
          </text>
        ))}
        <text
          x={(LEFT + RIGHT) / 2}
          y={height - 6}
          textAnchor="middle"
          className="fill-[#2d4a3e] text-[11px]"
        >
          {graph.xLabel}
        </text>
        <text
          x={10}
          y={(top + BOTTOM) / 2}
          textAnchor="middle"
          transform={`rotate(-90 10 ${String((top + BOTTOM) / 2)})`}
          className="fill-[#2d4a3e] text-[11px]"
        >
          {graph.yLabel}
        </text>
      </svg>
    </figure>
  );
}

function Symbol({ half }: { half: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="inline-block size-5 shrink-0"
    >
      {half ? (
        <path d="M10 2 A8 8 0 0 0 10 18 Z" fill={INK} />
      ) : (
        <circle cx="10" cy="10" r="8" fill={INK} />
      )}
    </svg>
  );
}

function PictureGraph({
  graph,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'picture_graph' }>;
}) {
  return (
    <figure className="m-0 max-w-md">
      <Title text={graph.title} />
      <table className="w-full border-collapse text-sm">
        <tbody>
          {graph.rows.map((row) => (
            <tr key={row.label} className="border-b border-line-strong">
              <th
                scope="row"
                className="w-28 py-1.5 pr-3 text-left font-semibold text-forest-950"
              >
                {row.label}
              </th>
              <td
                className="py-1.5"
                aria-label={`${String(row.count)} pictures`}
              >
                <span className="flex flex-wrap gap-1">
                  {Array.from({ length: Math.floor(row.count) }, (_, index) => (
                    <Symbol key={index} half={false} />
                  ))}
                  {row.count % 1 !== 0 && <Symbol half />}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <figcaption className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-forest-950">
        Key: <Symbol half={false} /> = {graph.key}
      </figcaption>
    </figure>
  );
}

function TallyMarks({ count }: { count: number }) {
  const groups = Math.floor(count / 5);
  const extra = count % 5;
  const group = (marks: number, crossed: boolean, key: string) => (
    <svg
      key={key}
      aria-hidden="true"
      viewBox="0 0 34 24"
      className="inline-block h-6 w-[34px] shrink-0"
    >
      {Array.from({ length: marks }, (_, index) => (
        <line
          key={index}
          x1={5 + index * 7}
          y1={3}
          x2={5 + index * 7}
          y2={21}
          stroke={INK}
          strokeWidth="2"
          strokeLinecap="round"
        />
      ))}
      {crossed && (
        <line
          x1={1}
          y1={19}
          x2={31}
          y2={5}
          stroke={INK}
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
  return (
    <span className="flex flex-wrap gap-2">
      {Array.from({ length: groups }, (_, index) =>
        group(4, true, `g${String(index)}`),
      )}
      {extra > 0 && group(extra, false, 'extra')}
    </span>
  );
}

function Tally({
  graph,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'tally' }>;
}) {
  return (
    <figure className="m-0 max-w-md">
      <Title text={graph.title} />
      <table className="w-full border-collapse text-sm">
        <tbody>
          {graph.rows.map((row) => (
            <tr key={row.label} className="border-b border-line-strong">
              <th
                scope="row"
                className="w-28 py-1.5 pr-3 text-left font-semibold text-forest-950"
              >
                {row.label}
              </th>
              <td
                className="py-1.5"
                aria-label={`${String(row.count)} tally marks`}
              >
                <TallyMarks count={row.count} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

type DotPlotGraph = Extract<GraphDiagramSpec, { type: 'dot_plot' }>;
type DotGroup = {
  name: string | null;
  dots: DotPlotGraph['dots'];
  fill: string;
};

function describeDots(dots: DotPlotGraph['dots']) {
  return dots
    .filter((dot) => dot.count > 0)
    .map((dot) => `${String(dot.value)}: ${String(dot.count)} dots`)
    .join('; ');
}

// One dot plot, or two (one above the other) on the same number scale so two
// groups can be compared. Each group has its own number line and its name
// above it; the second group's dots are in the second colour.
function DotPlot({ graph }: { graph: DotPlotGraph }) {
  const groups: DotGroup[] = [
    { name: graph.group, dots: graph.dots, fill: INK },
    ...(graph.second
      ? [{ name: graph.second.group, dots: graph.second.dots, fill: SECOND }]
      : []),
  ];
  const span = graph.max - graph.min;
  const xFor = (value: number) => 24 + ((value - graph.min) / span) * (W - 48);
  let top = 0;
  const panels = groups.map((group) => {
    const tallest = Math.max(...group.dots.map((dot) => dot.count), 1);
    const nameRoom = group.name ? 12 : 0;
    const panel = { ...group, top, line: top + nameRoom + 24 + tallest * 16 };
    top = panel.line + 36;
    return panel;
  });
  const lastLine = panels[panels.length - 1]?.line ?? 0;
  const height = lastLine + 44;
  const description = graph.second
    ? `A dot plot of ${graph.label} for two groups. ${groups
        .map((group) => `${group.name ?? ''}: ${describeDots(group.dots)}.`)
        .join(' ')}`
    : `A dot plot of ${graph.label}${graph.group ? ` (${graph.group})` : ''}. ${describeDots(graph.dots)}.`;
  return (
    <svg
      role="img"
      aria-label={description}
      viewBox={`0 0 ${String(W)} ${String(height)}`}
      className="h-auto w-full max-w-md"
    >
      {panels.map((panel, panelIndex) => (
        <g key={panelIndex}>
          {/* The name only, no coloured mark: a mark here could be read as
              one more dot above the first number. */}
          {panel.name && (
            <text
              x={14}
              y={panel.top + 14}
              className="fill-[#2d4a3e] text-[12px] font-semibold"
            >
              {panel.name}
            </text>
          )}
          <line
            x1={14}
            y1={panel.line}
            x2={W - 14}
            y2={panel.line}
            stroke={INK}
            strokeWidth="2"
          />
          {Array.from({ length: span + 1 }, (_, index) => {
            const value = graph.min + index;
            return (
              <g key={value}>
                <line
                  x1={xFor(value)}
                  y1={panel.line}
                  x2={xFor(value)}
                  y2={panel.line + 6}
                  stroke={INK}
                  strokeWidth="2"
                />
                <text
                  x={xFor(value)}
                  y={panel.line + 20}
                  textAnchor="middle"
                  className="fill-[#2d4a3e] text-[11px] font-semibold"
                >
                  {value}
                </text>
              </g>
            );
          })}
          {panel.dots.map((dot) =>
            Array.from({ length: dot.count }, (_, index) => (
              <circle
                key={`${String(dot.value)}-${String(index)}`}
                cx={xFor(dot.value)}
                cy={panel.line - 10 - index * 16}
                r="6"
                fill={panel.fill}
              />
            )),
          )}
        </g>
      ))}
      <text
        x={W / 2}
        y={height - 6}
        textAnchor="middle"
        className="fill-[#2d4a3e] text-[11px]"
      >
        {graph.label}
      </text>
    </svg>
  );
}

function PieChart({
  graph,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'pie_chart' }>;
}) {
  const cx = 90;
  const cy = 90;
  const r = 78;
  const point = (part: number, radius = r) => {
    const angle = (part / graph.parts) * 2 * Math.PI - Math.PI / 2;
    return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  };
  let start = 0;
  const fills = sectorFills(graph.sectors.map((sector) => sector.label));
  const sectors = graph.sectors.map((sector, index) => {
    const from = start;
    start += sector.parts;
    const [x1, y1] = point(from);
    const [x2, y2] = point(start);
    const large = sector.parts / graph.parts > 0.5 ? 1 : 0;
    return {
      ...sector,
      fill: fills[index],
      path: `M ${String(cx)} ${String(cy)} L ${String(x1)} ${String(y1)} A ${String(r)} ${String(r)} 0 ${String(large)} 1 ${String(x2)} ${String(y2)} Z`,
    };
  });
  const description = `A pie chart split into ${String(graph.parts)} equal parts. ${graph.sectors
    .map((sector) => `${sector.label}: ${String(sector.parts)} parts`)
    .join('; ')}.`;
  return (
    <figure className="m-0 max-w-md">
      <Title text={graph.title} />
      <div className="flex flex-wrap items-center gap-4">
        <svg
          role="img"
          aria-label={description}
          viewBox="0 0 180 180"
          className="h-auto w-44 shrink-0"
        >
          {sectors.map((sector) => (
            <path
              key={sector.label}
              d={sector.path}
              fill={sector.fill}
              stroke={INK}
              strokeWidth="2"
            />
          ))}
          {/* The equal parts, so a child can count them. */}
          {Array.from({ length: graph.parts }, (_, index) => {
            const [x, y] = point(index);
            return (
              // White under ink dashes, so the marks show on dark and on
              // light sectors alike.
              <g key={index}>
                <line
                  x1={cx}
                  y1={cy}
                  x2={x}
                  y2={y}
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
                <line
                  x1={cx}
                  y1={cy}
                  x2={x}
                  y2={y}
                  stroke={INK}
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                />
              </g>
            );
          })}
        </svg>
        <ul className="m-0 grid list-none gap-1.5 p-0 text-sm font-semibold text-forest-950">
          {sectors.map((sector) => (
            <li key={sector.label} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block size-4 shrink-0 border border-forest-950"
                style={{ backgroundColor: sector.fill }}
              />
              {sector.label}
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

function DataTable({
  graph,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'table' }>;
}) {
  return (
    <figure className="m-0 max-w-md overflow-x-auto">
      <table className="w-full border-collapse text-sm text-forest-950">
        {graph.title && (
          <caption className="mb-1 text-left font-semibold">
            {graph.title}
          </caption>
        )}
        <thead>
          <tr>
            {graph.headers.map((header, index) => (
              <th
                key={index}
                scope="col"
                className="border border-line-strong bg-sage px-2 py-1.5 text-left font-semibold"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {graph.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) =>
                cellIndex === 0 ? (
                  <th
                    key={cellIndex}
                    scope="row"
                    className="border border-line-strong px-2 py-1.5 text-left font-semibold"
                  >
                    {cell}
                  </th>
                ) : (
                  <td
                    key={cellIndex}
                    className="border border-line-strong px-2 py-1.5"
                  >
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {graph.note && (
        <figcaption className="mt-1.5 text-sm text-forest-950">
          {graph.note}
        </figcaption>
      )}
    </figure>
  );
}

// Spoken with words for the signs, so "-2" is never read as "dash 2".
function spokenCoordinate(value: number): string {
  return value < 0 ? `negative ${String(-value)}` : String(value);
}

const fixed = (value: number) => value.toFixed(2);

type Box = { x1: number; y1: number; x2: number; y2: number };
type Spot = { x: number; y: number };

// Whether a straight piece from a to b passes through a box (both in
// pixels): either end inside, or it crosses one of the box's sides.
function pieceMeetsBox(a: Spot, b: Spot, box: Box): boolean {
  const inside = (p: Spot) =>
    p.x >= box.x1 && p.x <= box.x2 && p.y >= box.y1 && p.y <= box.y2;
  if (inside(a) || inside(b)) return true;
  const cross = (p: Spot, q: Spot, r: Spot, s: Spot) => {
    const turn = (u: Spot, v: Spot, w: Spot) =>
      (v.x - u.x) * (w.y - u.y) - (v.y - u.y) * (w.x - u.x);
    return (
      turn(p, q, r) * turn(p, q, s) <= 0 && turn(r, s, p) * turn(r, s, q) <= 0
    );
  };
  const corners = [
    { x: box.x1, y: box.y1 },
    { x: box.x2, y: box.y1 },
    { x: box.x2, y: box.y2 },
    { x: box.x1, y: box.y2 },
  ];
  return corners.some((corner, index) =>
    cross(a, b, corner, corners[(index + 1) % 4] ?? corner),
  );
}

function boxesMeet(a: Box, b: Box): boolean {
  return a.x1 < b.x2 && b.x1 < a.x2 && a.y1 < b.y2 && b.y1 < a.y2;
}

// A line's drawn piece (where it meets the grid's edges) and where its
// label goes: beside one end, inside the picture, clear of the two axes and
// their numbers, the other lines, the points and the other labels. The ends
// are tried top end first (right end for a flat line), each a few steps in
// along the line and on either side of it; if nothing is clear, the spot
// with the fewest clashes is used, and `clashes` says how many there were.
function layGridLines(
  graph: CoordinateGridSpec,
  xFor: (x: number) => number,
  yFor: (y: number) => number,
  width: number,
) {
  const pieces = graph.lines.map((line) => {
    const [a, b] = clipGridLine(graph, line);
    return [
      { x: xFor(a.x), y: yFor(a.y) },
      { x: xFor(b.x), y: yFor(b.y) },
    ] as const;
  });
  const byLabel = new Map(graph.points.map((point) => [point.label, point]));
  const others: (readonly [Spot, Spot])[] = [...pieces];
  for (const shape of graph.shapes) {
    const corners = shape
      .map((label) => byLabel.get(label))
      .filter((point) => point !== undefined)
      .map((point) => ({ x: xFor(point.x), y: yFor(point.y) }));
    corners.forEach((corner, index) => {
      const next = corners[(index + 1) % corners.length];
      if (next && (shape.length > 2 || index === 0)) {
        others.push([corner, next]);
      }
    });
  }
  // A point's letter sits up and to the right of it, as on every grid. A
  // point on one of the lines may have its letter crossed by the line, so
  // then the letter tries below right, up left and below left in turn.
  const pointLabels = graph.points.map((point) => {
    const px = xFor(point.x);
    const py = yFor(point.y);
    const w = point.label.length * 8;
    const tries = [
      { x: px + 7, y: py - 6, anchor: 'start' as const },
      { x: px + 7, y: py + 15, anchor: 'start' as const },
      { x: px - 7, y: py - 6, anchor: 'end' as const },
      { x: px - 7, y: py + 15, anchor: 'end' as const },
    ];
    const boxOf = (spot: (typeof tries)[number]) => ({
      x1: spot.anchor === 'start' ? spot.x - 1 : spot.x - w - 1,
      y1: spot.y - 11,
      x2: spot.anchor === 'start' ? spot.x + w + 1 : spot.x + 1,
      y2: spot.y + 3,
    });
    const clear = tries.find((spot) =>
      pieces.every(([p, q]) => !pieceMeetsBox(p, q, boxOf(spot))),
    );
    const spot = clear ?? tries[0] ?? { x: px + 7, y: py - 6, anchor: 'start' };
    return { ...spot, box: boxOf(spot) };
  });
  // The axes with their numbers, and every point with its letter.
  const blocked: Box[] = [
    { x1: xFor(0) - 24, y1: 0, x2: xFor(0) + 3, y2: yFor(graph.yMin) + 30 },
    { x1: 0, y1: yFor(0) - 3, x2: width, y2: yFor(0) + 17 },
    ...graph.points.map((point) => ({
      x1: xFor(point.x) - 6,
      y1: yFor(point.y) - 6,
      x2: xFor(point.x) + 6,
      y2: yFor(point.y) + 6,
    })),
    ...pointLabels.map((label) => label.box),
  ];
  const area: Box = {
    x1: xFor(graph.xMin),
    y1: 2,
    x2: width - 2,
    y2: yFor(graph.yMin),
  };
  let clashCount = 0;
  const laid = graph.lines.map((line, index) => {
    const piece = pieces[index] ?? pieces[0];
    if (!piece || line.label === null) return { piece, label: null };
    const text = line.label;
    const w = text.length * 7 + 2;
    const h = 13;
    const [a, b] = piece;
    const flat = Math.abs(a.y - b.y) < 0.5;
    const ends = (flat ? a.x > b.x : a.y < b.y) ? [a, b] : [b, a];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    // Label centres to try, best first.
    const centres: Spot[] = [];
    for (const end of ends) {
      const other = end === a ? b : a;
      const ux = (other.x - end.x) / length;
      const uy = (other.y - end.y) / length;
      for (const step of [8, 22, 40, 60, 85, 115]) {
        if (step > Math.max(8, length * 0.45)) break;
        for (const side of [1, -1]) {
          const nx = -uy * side;
          const ny = ux * side;
          const reach = 6 + (Math.abs(nx) * w) / 2 + (Math.abs(ny) * h) / 2;
          centres.push({
            x: end.x + ux * step + nx * reach,
            y: end.y + uy * step + ny * reach,
          });
        }
      }
      // An end on the right edge may take its label just past the edge,
      // when the picture has been widened to make room.
      if (Math.abs(end.x - xFor(graph.xMax)) < 0.5) {
        centres.push({ x: end.x + 6 + w / 2, y: end.y });
      }
    }
    let best: { spot: Spot; box: Box; clashes: number } | null = null;
    for (const centre of centres) {
      const box = {
        x1: centre.x - w / 2,
        y1: centre.y - h / 2,
        x2: centre.x + w / 2,
        y2: centre.y + h / 2,
      };
      const outside =
        box.x1 < area.x1 ||
        box.x2 > area.x2 ||
        box.y1 < area.y1 ||
        box.y2 > area.y2;
      const clashes =
        (outside ? 10 : 0) +
        blocked.filter((block) => boxesMeet(block, box)).length +
        others.filter(([p, q]) => pieceMeetsBox(p, q, box)).length;
      if (!best || clashes < best.clashes) {
        best = { spot: centre, box, clashes };
      }
      if (clashes === 0) break;
    }
    if (!best) return { piece, label: null };
    clashCount += best.clashes;
    blocked.push(best.box);
    return { piece, label: { text, x: best.spot.x, y: best.spot.y + 3.5 } };
  });
  return { lines: laid, pointLabels, clashes: clashCount };
}

// The same drawing is the answer control for "tap a point on the grid"
// questions (PracticeTask.tsx): given `onToggle`, every crossing of the grid
// lines also gets a real button over it.
export function CoordinateGrid({
  graph,
  chosen = [],
  disabled = false,
  onToggle,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'coordinate_grid' }>;
  // The tapped crossings.
  chosen?: { x: number; y: number }[];
  disabled?: boolean;
  // Given only where the grid is the answer control.
  onToggle?: ((x: number, y: number) => void) | undefined;
}) {
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const across = graph.xMax - graph.xMin;
  const up = graph.yMax - graph.yMin;
  const cell = Math.min(26, Math.floor(280 / Math.max(across, up)));
  const left = 34;
  const top = 16;
  const height = top + up * cell + 30;
  const xFor = (x: number) => left + (x - graph.xMin) * cell;
  const yFor = (y: number) => top + (graph.yMax - y) * cell;
  const byLabel = new Map(graph.points.map((point) => [point.label, point]));
  const description = describeCoordinateGrid(graph);
  // When a line's label has no clear spot inside the grid (a narrow grid
  // with several rules), the picture is widened so labels can sit just past
  // its right edge.
  let width = left + across * cell + 22;
  let laid = layGridLines(graph, xFor, yFor, width);
  if (laid.clashes > 0) {
    const room = Math.max(
      ...graph.lines.map((line) => (line.label ?? '').length * 7 + 10),
    );
    const wider = layGridLines(graph, xFor, yFor, width + room);
    if (wider.clashes < laid.clashes) {
      width += room;
      laid = wider;
    }
  }
  const drawnLines = laid.lines;
  const xs = Array.from(
    { length: across + 1 },
    (_, index) => graph.xMin + index,
  );
  const ys = Array.from({ length: up + 1 }, (_, index) => graph.yMin + index);
  // With negative numbers the labels sit along the two axes, as on a real
  // number plane; otherwise along the bottom and left edges.
  const labelY = yFor(0) + 14;
  const labelX = xFor(0) - 7;

  return (
    <figure className="m-0">
      <Title text={graph.title} />
      <div
        role={onToggle ? 'group' : undefined}
        aria-label={onToggle ? description : undefined}
        className="relative w-full max-w-sm"
      >
        <svg
          role={onToggle ? undefined : 'img'}
          aria-label={onToggle ? undefined : description}
          aria-hidden={onToggle ? true : undefined}
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          className="block h-auto w-full"
        >
          {xs.map((x) => (
            <line
              key={`v${String(x)}`}
              x1={xFor(x)}
              y1={yFor(graph.yMin)}
              x2={xFor(x)}
              y2={yFor(graph.yMax)}
              stroke={x === 0 ? INK : GRID}
              strokeWidth={x === 0 ? 2 : 1}
            />
          ))}
          {ys.map((y) => (
            <line
              key={`h${String(y)}`}
              x1={xFor(graph.xMin)}
              y1={yFor(y)}
              x2={xFor(graph.xMax)}
              y2={yFor(y)}
              stroke={y === 0 ? INK : GRID}
              strokeWidth={y === 0 ? 2 : 1}
            />
          ))}
          {xs.map((x) =>
            x === 0 && graph.yMin < 0 ? null : (
              <text
                key={`x${String(x)}`}
                x={xFor(x)}
                y={labelY}
                textAnchor="middle"
                className="fill-[#2d4a3e] text-[10px] font-semibold"
              >
                {x}
              </text>
            ),
          )}
          {ys.map((y) =>
            y === 0 ? null : (
              <text
                key={`y${String(y)}`}
                x={labelX}
                y={yFor(y) + 3.5}
                textAnchor="end"
                className="fill-[#2d4a3e] text-[10px] font-semibold"
              >
                {y}
              </text>
            ),
          )}
          <text
            x={xFor(graph.xMax) + 8}
            y={yFor(0) + 4}
            className="fill-[#2d4a3e] text-[11px] italic"
          >
            x
          </text>
          <text
            x={xFor(0)}
            y={yFor(graph.yMax) - 6}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-[11px] italic"
          >
            y
          </text>
          {graph.mirror && (
            <line
              x1={
                graph.mirror.direction === 'vertical'
                  ? xFor(graph.mirror.at)
                  : xFor(graph.xMin)
              }
              y1={
                graph.mirror.direction === 'vertical'
                  ? yFor(graph.yMin)
                  : yFor(graph.mirror.at)
              }
              x2={
                graph.mirror.direction === 'vertical'
                  ? xFor(graph.mirror.at)
                  : xFor(graph.xMax)
              }
              y2={
                graph.mirror.direction === 'vertical'
                  ? yFor(graph.yMax)
                  : yFor(graph.mirror.at)
              }
              stroke={SECOND}
              strokeWidth="2.5"
              strokeDasharray="7 5"
            />
          )}
          {/* Straight lines run edge to edge; a line's two points are not
            marked, so where it crosses an axis is read off the grid. */}
          {drawnLines.map(({ piece }, index) =>
            piece ? (
              <line
                key={`line${String(index)}`}
                data-grid-line="true"
                x1={fixed(piece[0].x)}
                y1={fixed(piece[0].y)}
                x2={fixed(piece[1].x)}
                y2={fixed(piece[1].y)}
                stroke={INK}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            ) : null,
          )}
          {graph.shapes.map((shape, index) => {
            const corners = shape
              .map((label) => byLabel.get(label))
              .filter((point) => point !== undefined)
              .map(
                (point) => `${String(xFor(point.x))},${String(yFor(point.y))}`,
              )
              .join(' ');
            return shape.length > 2 ? (
              <polygon
                key={index}
                points={corners}
                fill="#9fc3b1"
                fillOpacity="0.35"
                stroke={INK}
                strokeWidth="2"
              />
            ) : (
              <polyline
                key={index}
                points={corners}
                fill="none"
                stroke={INK}
                strokeWidth="2"
              />
            );
          })}
          {graph.points.map((point, index) => (
            <g key={point.label}>
              <circle
                cx={xFor(point.x)}
                cy={yFor(point.y)}
                r="4.5"
                fill={INK}
              />
              <text
                x={laid.pointLabels[index]?.x ?? xFor(point.x) + 7}
                y={laid.pointLabels[index]?.y ?? yFor(point.y) - 6}
                textAnchor={
                  laid.pointLabels[index]?.anchor === 'end' ? 'end' : undefined
                }
                className="fill-[#2d4a3e] text-[12px] font-bold"
                stroke="#ffffff"
                strokeWidth="3"
                paintOrder="stroke"
              >
                {point.label}
              </text>
            </g>
          ))}
          {drawnLines.map(({ label }, index) =>
            label ? (
              <text
                key={`label${String(index)}`}
                x={fixed(label.x)}
                y={fixed(label.y)}
                textAnchor="middle"
                className="fill-[#2d4a3e] text-[12px] font-bold"
                stroke="#ffffff"
                strokeWidth="3"
                paintOrder="stroke"
              >
                {label.text}
              </text>
            ) : null,
          )}
          {/* A tapped crossing: a filled dot with a ring round it, so it is
            told apart by shape and not by colour alone. Its coordinates are
            not printed: reading them off the grid is the question. */}
          {chosen.map((point) => (
            <g key={`${String(point.x)},${String(point.y)}`} data-chosen="true">
              <circle
                cx={xFor(point.x)}
                cy={yFor(point.y)}
                r="8.5"
                fill="none"
                stroke={INK}
                strokeWidth="2"
              />
              <circle
                cx={xFor(point.x)}
                cy={yFor(point.y)}
                r="4.5"
                fill={INK}
              />
            </g>
          ))}
        </svg>
        {onToggle &&
          ys
            .slice()
            .reverse()
            .flatMap((y) =>
              xs.map((x) => {
                const key = `${String(x)},${String(y)}`;
                const isChosen = chosen.some(
                  (point) => point.x === x && point.y === y,
                );
                // One crossing takes Tab: the first tapped one, or the origin.
                const first = chosen[0] ?? {
                  x: Math.min(Math.max(0, graph.xMin), graph.xMax),
                  y: Math.min(Math.max(0, graph.yMin), graph.yMax),
                };
                const move = (event: KeyboardEvent<HTMLButtonElement>) => {
                  const step: Record<string, [number, number]> = {
                    ArrowLeft: [-1, 0],
                    ArrowRight: [1, 0],
                    ArrowUp: [0, 1],
                    ArrowDown: [0, -1],
                  };
                  const by = step[event.key];
                  if (!by) return;
                  event.preventDefault();
                  buttons.current
                    .get(`${String(x + by[0])},${String(y + by[1])}`)
                    ?.focus();
                };
                return (
                  <button
                    key={key}
                    ref={(node) => {
                      if (node) buttons.current.set(key, node);
                      else buttons.current.delete(key);
                    }}
                    type="button"
                    aria-label={`x ${spokenCoordinate(x)}, y ${spokenCoordinate(y)}`}
                    aria-pressed={isChosen}
                    disabled={disabled}
                    tabIndex={first.x === x && first.y === y ? 0 : -1}
                    className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-transparent focus-visible:outline-3 focus-visible:outline-offset-0 focus-visible:outline-forest-600"
                    style={{
                      left: `${String((xFor(x) / width) * 100)}%`,
                      top: `${String((yFor(y) / height) * 100)}%`,
                      width: `${String((cell / width) * 100)}%`,
                      height: `${String((cell / height) * 100)}%`,
                    }}
                    onClick={() => {
                      onToggle(x, y);
                    }}
                    onKeyDown={move}
                  />
                );
              }),
            )}
      </div>
    </figure>
  );
}

function MapGrid({
  graph,
}: {
  graph: Extract<GraphDiagramSpec, { type: 'map_grid' }>;
}) {
  const cell = Math.min(46, Math.floor(300 / graph.columns));
  const left = 24;
  const top = 6;
  const width = left + graph.columns * cell + 6;
  const height = top + graph.rows * cell + 22;
  const letter = (column: number) => String.fromCharCode(64 + column);
  const yForRow = (row: number) =>
    top + (graph.rowOneAt === 'top' ? row - 1 : graph.rows - row) * cell;
  const description = `A map grid with columns A to ${letter(graph.columns)} and rows 1 to ${String(graph.rows)}, row 1 at the ${graph.rowOneAt}. ${graph.items
    .map((item) => `${item.label} in ${letter(item.column)}${String(item.row)}`)
    .join('; ')}.`;
  const columns = Array.from(
    { length: graph.columns },
    (_, index) => index + 1,
  );
  const rows = Array.from({ length: graph.rows }, (_, index) => index + 1);
  return (
    <figure className="m-0">
      <Title text={graph.title} />
      <svg
        role="img"
        aria-label={description}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        className="h-auto w-full max-w-sm"
      >
        {columns.map((column) =>
          rows.map((row) => (
            <rect
              key={`${String(column)}-${String(row)}`}
              x={left + (column - 1) * cell}
              y={yForRow(row)}
              width={cell}
              height={cell}
              fill="#ffffff"
              stroke={INK}
              strokeWidth="1"
            />
          )),
        )}
        {columns.map((column) => (
          <text
            key={`c${String(column)}`}
            x={left + (column - 0.5) * cell}
            y={top + graph.rows * cell + 15}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-[12px] font-bold"
          >
            {letter(column)}
          </text>
        ))}
        {rows.map((row) => (
          <text
            key={`r${String(row)}`}
            x={left - 8}
            y={yForRow(row) + cell / 2 + 4}
            textAnchor="end"
            className="fill-[#2d4a3e] text-[12px] font-bold"
          >
            {row}
          </text>
        ))}
        {graph.items.map((item) => (
          <g key={item.label}>
            <rect
              x={left + (item.column - 1) * cell + 2}
              y={yForRow(item.row) + 2}
              width={cell - 4}
              height={cell - 4}
              rx="4"
              fill="#d1fae5"
            />
            <text
              x={left + (item.column - 0.5) * cell}
              y={yForRow(item.row) + cell / 2 + 3.5}
              textAnchor="middle"
              className="fill-[#113024] font-semibold"
              style={{
                fontSize: Math.min(11, (cell - 6) / (item.label.length * 0.56)),
              }}
            >
              {item.label}
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}

export function GraphDiagramView({ graph }: { graph: GraphDiagramSpec }) {
  switch (graph.type) {
    case 'column_graph':
    case 'line_chart':
      return <ScaledGraphView graph={graph} />;
    case 'picture_graph':
      return <PictureGraph graph={graph} />;
    case 'tally':
      return <Tally graph={graph} />;
    case 'dot_plot':
      return <DotPlot graph={graph} />;
    case 'pie_chart':
      return <PieChart graph={graph} />;
    case 'table':
      return <DataTable graph={graph} />;
    case 'coordinate_grid':
      return <CoordinateGrid graph={graph} />;
    case 'map_grid':
      return <MapGrid graph={graph} />;
    case 'number_line':
      return <NumberLineView line={graph} />;
    case 'fraction_model':
      return <FractionModelView model={graph} />;
    case 'shade_grid':
      return <ShadeGridView grid={graph} />;
    case 'angle':
    case 'angles_at_point':
    case 'transversal':
      return <AnglePictureView angle={graph} />;
    case 'shape_2d':
    case 'composite_shape':
      return <ShapePictureView shape={graph} />;
    case 'solid_3d':
    case 'net':
    case 'net_grid':
      return <SolidPictureView solid={graph} />;
    case 'circle_parts':
      return <CirclePartsView circle={graph} />;
    case 'cube_stack':
      return <CubeStackView stack={graph} />;
    case 'curved_shape':
      return <CurvedShapeView shape={graph} />;
    case 'shape_pair':
      return <ShapePairView pair={graph} />;
    case 'quad_diagonals':
      return <QuadDiagonalsView quad={graph} />;
    case 'tree_diagram':
      return <TreeDiagramView tree={graph} />;
    case 'venn':
      return <VennView venn={graph} />;
    case 'axes_3d':
      return <Axes3dView axes={graph} />;
    case 'thermometer':
    case 'digital_clock':
      return <InstrumentPictureView instrument={graph} />;
    case 'area_model':
      return <AreaModelView model={graph} />;
    case 'clock':
      return <ClockPictureView clock={graph} />;
    case 'base_ten':
    case 'counters':
    case 'array':
      return <CountPictureView count={graph} />;
    case 'money':
      return <MoneyPictureView money={graph} />;
    case 'ruler':
    case 'jug':
    case 'scale':
      return <MeasurePictureView measure={graph} />;
    case 'bar_model':
      return <BarModelPictureView barModel={graph} />;
    case 'picture_pattern':
      return <PicturePatternView pattern={graph} />;
    case 'cups':
    case 'unit_line':
    case 'balance':
      return <InformalMeasureView measure={graph} />;
    case 'turn_picture':
      return <TurnPictureView turn={graph} />;
    case 'ten_frame':
      return <TenFrameView frame={graph} />;
    case 'hundred_chart':
      return <HundredChartView chart={graph} />;
    case 'line_up':
      return <LineUpView lineUp={graph} />;
    case 'everyday_object':
      return <EverydayObjectView thing={graph} />;
  }
}
